import { nanoid } from 'nanoid';

import { FOLDER_ID_CUSTOM, QUESTION_TYPES } from '@api/instant_survey';
import {
  LANGUAGE_SCREENER_QUESTION_TEXT,
  LANGUAGE_SCREENER_QUESTION_TEXT_TRANSLATIONS,
  LANGUAGE_SCREENER_SCREENER_LABEL,
  SURVEY_SOURCE_LANGUAGE,
} from '@constants';
import {
  getOrderedSurveyLanguages,
  getSurveyLanguageSelection,
  getSurveyNativeLanguageLabel,
} from '@helpers';
import {
  getQuestionsWithScreenersFirst,
  isLanguageScreenerQuestion,
} from '../../../../../utilities/helpers';

const getAnswerLanguageCode = (answer) => answer.properties?.languageCode;

// Builds native-language options, preserving saved rows and marking removed saved rows for deletion.
const buildAnswers = (languages, existingAnswers = []) => {
  const languageEntries = languages.map((code) => ({
    code,
    label: getSurveyNativeLanguageLabel(code),
  }));
  const desiredCodes = new Set(languages);
  const desiredLabels = new Set(languageEntries.map(({ label }) => label));
  const activeByCode = new Map(
    existingAnswers
      .filter((answer) => !answer._delete && getAnswerLanguageCode(answer))
      .map((answer) => [getAnswerLanguageCode(answer), answer])
  );
  const activeByLabel = new Map(
    existingAnswers.filter((answer) => !answer._delete).map((answer) => [answer.text, answer])
  );

  const answers = languageEntries.map(({ code, label }) => {
    const existing = activeByCode.get(code) || activeByLabel.get(label);
    return existing
      ? {
          ...existing,
          properties: { ...existing.properties, languageCode: code },
        }
      : {
          id: nanoid(),
          properties: { languageCode: code },
          richText: null,
          text: label,
          title: label,
          value: label,
        };
  });

  existingAnswers.forEach((answer) => {
    if (typeof answer.id !== 'number') return;
    if (answer._delete) {
      // Retain saved removed answers so the next question update deletes them.
      answers.push(answer);
    } else if (
      getAnswerLanguageCode(answer)
        ? !desiredCodes.has(getAnswerLanguageCode(answer))
        : !desiredLabels.has(answer.text)
    ) {
      answers.push({ ...answer, _delete: true });
    }
  });

  return answers;
};

/**
 * Builds prompt translations for target languages, preserving saved rows by languageCode.
 * @param {String[]} languages target language codes; excludes the survey source language
 * @param {Object[]} [existingTranslations]
 * @return {Object[]}
 */
const buildLanguageScreenerTranslations = (languages, existingTranslations = []) => {
  const desired = new Set(languages);
  const activeByCode = new Map(
    existingTranslations
      .filter((entry) => !entry._delete)
      .map((entry) => [entry.languageCode, entry])
  );

  const translations = languages.map((code) => {
    if (activeByCode.has(code)) return activeByCode.get(code);
    const text =
      LANGUAGE_SCREENER_QUESTION_TEXT_TRANSLATIONS[code] || LANGUAGE_SCREENER_QUESTION_TEXT;
    return { languageCode: code, richText: text, text };
  });

  existingTranslations.forEach((entry) => {
    if (typeof entry.id !== 'number') return;
    if (entry._delete) translations.push(entry);
    else if (!desired.has(entry.languageCode)) translations.push({ ...entry, _delete: true });
  });

  return translations;
};

const getTargetLanguages = (languageInfo) => getOrderedSurveyLanguages(languageInfo).slice(1);

/**
 * Gets the language screener question text for the survey's source language.
 */
const getTranslatedScreenerQuestionText = (languageInfo) => {
  const [sourceLanguage] = getOrderedSurveyLanguages(languageInfo);
  return (
    LANGUAGE_SCREENER_QUESTION_TEXT_TRANSLATIONS[sourceLanguage] || LANGUAGE_SCREENER_QUESTION_TEXT
  );
};

const getQuestionsWithLanguageScreenerFirst = (questions = []) => {
  const orderedQuestions = getQuestionsWithScreenersFirst(questions);
  return [
    ...orderedQuestions.filter(
      (question) => !question._delete && isLanguageScreenerQuestion(question)
    ),
    ...orderedQuestions.filter(
      (question) => question._delete || !isLanguageScreenerQuestion(question)
    ),
  ];
};

/**
 * Builds createQuestion input for a new language screener.
 * @param {import('@api/instant_survey/types').InstantSurvey['languageInfo']} languageInfo
 * @param {?(number|string)} [instantSurveyTypeId]
 * @return {Object}
 */
export const buildLanguageScreenerCreateInput = (languageInfo, instantSurveyTypeId) => ({
  answers: buildAnswers(getOrderedSurveyLanguages(languageInfo)),
  concepts: [],
  folder: FOLDER_ID_CUSTOM,
  instantSurveyTypes: instantSurveyTypeId ? [instantSurveyTypeId] : [],
  isScreener: true,
  properties: { isLanguageScreener: true },
  screenerLabel: LANGUAGE_SCREENER_SCREENER_LABEL,
  showConceptImage: false,
  subQuestions: [],
  text: getTranslatedScreenerQuestionText(languageInfo),
  translations: buildLanguageScreenerTranslations(getTargetLanguages(languageInfo)),
  type: QUESTION_TYPES.RADIO.value,
});

/**
 * Places a freshly-created language screener ahead of other screeners.
 * @param {import('@api/instant_survey/types').Question} createdQuestion
 * @param {import('@api/instant_survey/types').Question[]} baseQuestions the plan's remaining questions
 * @return {import('@api/instant_survey/types').Question[]}
 */
export const insertLanguageScreenerFirst = (createdQuestion, baseQuestions = []) =>
  getQuestionsWithLanguageScreenerFirst([createdQuestion, ...baseQuestions]);

/**
 * Reconciles the managed language screener with languageInfo.
 *
 * @param {import('@api/instant_survey/types').Question[]} [questions] the survey's full questions
 *   array, including `_delete: true` records that still need to be sent to the backend
 * @param {import('@api/instant_survey/types').InstantSurvey['languageInfo']} languageInfo
 * @return {{ type: 'none' }
 *   | { type: 'set', questions: import('@api/instant_survey/types').Question[] }
 *   | { type: 'create', baseQuestions: import('@api/instant_survey/types').Question[] }}
 */
export const planLanguageScreenerReconciliation = (questions = [], languageInfo) => {
  const { translatedLanguages } = getSurveyLanguageSelection(languageInfo, {
    fallbackSourceLanguage: SURVEY_SOURCE_LANGUAGE,
  });
  const shouldHaveLanguageScreener = translatedLanguages.length > 0;
  const activeScreeners = questions.filter((q) => isLanguageScreenerQuestion(q) && !q._delete);

  // Keep the first backend-persisted screener as the canonical one.
  const canonical = shouldHaveLanguageScreener
    ? activeScreeners.find((q) => typeof q.id === 'number') || null
    : null;

  // Mark saved duplicates for deletion; drop never-saved duplicates outright.
  const toRemove = new Set(activeScreeners.filter((q) => q !== canonical));
  const removedQuestions = [...toRemove]
    .filter((q) => typeof q.id === 'number')
    .map((q) => ({ ...q, _delete: true }));
  const kept = questions.filter((q) => !toRemove.has(q));

  if (!shouldHaveLanguageScreener) {
    if (!activeScreeners.length) return { type: 'none' };
    return {
      questions: getQuestionsWithScreenersFirst([...kept, ...removedQuestions]),
      type: 'set',
    };
  }

  if (!canonical) {
    return { baseQuestions: [...kept, ...removedQuestions], type: 'create' };
  }

  const hasRemovals = toRemove.size > 0;

  // Re-sync when prompt/options no longer match the language selection.
  const desiredLabels = getOrderedSurveyLanguages(languageInfo).map(getSurveyNativeLanguageLabel);
  const activeLabels = (canonical.answers || [])
    .filter((answer) => !answer._delete)
    .map((answer) => answer.text);
  const isSynced =
    canonical.text === getTranslatedScreenerQuestionText(languageInfo) &&
    desiredLabels.length === activeLabels.length &&
    desiredLabels.every((label, index) => label === activeLabels[index]);

  const updated = isSynced
    ? canonical
    : {
        ...canonical,
        answers: buildAnswers(getOrderedSurveyLanguages(languageInfo), canonical.answers || []),
        text: getTranslatedScreenerQuestionText(languageInfo),
        translations: buildLanguageScreenerTranslations(
          getTargetLanguages(languageInfo),
          canonical.translations || []
        ),
      };

  const next = [...kept.map((q) => (q === canonical ? updated : q)), ...removedQuestions];
  const orderedNext = getQuestionsWithLanguageScreenerFirst(next);
  const hasOrderChange = orderedNext.some((question, index) => question !== questions[index]);

  if (isSynced && !hasRemovals && !hasOrderChange) return { type: 'none' };

  return { questions: orderedNext, type: 'set' };
};
